import React, { useState, useEffect } from 'react';
import { FileText, Download, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import p45Service from '../../api/services/p45Service';
import useEmployeeProfile from "../../hooks/useEmployeeProfile";

const EmployeeP45 = ({ hideHeader = false }) => {
  const { setHeader } = usePageHeader();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Get current employee data from API
  const employeeProfile = useEmployeeProfile();
  const {
    employeeNo,
    displayName: employeeName,
    isLoading: isLoadingProfile,
    error: profileError,
    TerminationDate,
  } = employeeProfile;

  // Check if employee is terminated (TerminationDate is not the default 1900-01-01)
  const isTerminated =
    TerminationDate && TerminationDate !== "1900-01-01T00:00:00Z";

  // Log for debugging
  useEffect(() => {
    if (employeeNo) {
      console.log(
        "📋 P45 Component - Using Employee No:",
        employeeNo,
        "Name:",
        employeeName,
      );
      console.log(
        "📋 Termination Date:",
        TerminationDate,
        "Is Terminated:",
        isTerminated,
      );
    }
  }, [employeeNo, employeeName, TerminationDate, isTerminated]);

  useEffect(() => {
    if (!hideHeader) {
      setHeader({
        icon: FileText,
        title: "P45 Document",
        subtitle: "Generate and download your P45 tax form",
      });
    }

    // Cleanup PDF URL on unmount
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      if (!hideHeader) {
        setHeader({ icon: null, title: "", subtitle: "" });
      }
    };
  }, [hideHeader, setHeader, pdfUrl]);

  const handleGenerateP45 = async () => {
    if (!employeeNo) {
      setError("Employee number not found. Please ensure you are logged in.");
      return;
    }

    if (!isTerminated) {
      setError("P45 can only be generated for terminated employees.");
      return;
    }

    console.log("🚀 Generating P45 for employee:", employeeNo);
    setIsGenerating(true);
    setError(null);
    setSuccess(false);
    setPdfBase64(null);

    // Cleanup previous PDF URL
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }

    try {
      // Generate P45 document
      const base64Data = await p45Service.generateP45Document(employeeNo);

      setPdfBase64(base64Data);
      setSuccess(true);

      // Create blob URL for PDF preview
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error("Error generating P45:", err);

      // Show user-friendly error message
      let userFriendlyError = "We're unable to generate your P45 document at the moment. ";

      if (err.response?.status === 404 || err.message?.includes("not found")) {
        userFriendlyError += "No P45 data was found for your account. Please contact HR support.";
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        userFriendlyError += "You don't have permission to generate this document. Please contact HR support.";
      } else if (err.response?.status >= 500) {
        userFriendlyError += "Our system is experiencing technical difficulties. Please try again later or contact HR support.";
      } else {
        userFriendlyError += "Please try again or contact HR support if the problem persists.";
      }

      setError(userFriendlyError);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBase64) {
      setError("No document available to download");
      return;
    }

    try {
      p45Service.downloadP45Document(pdfBase64, employeeNo);
    } catch (err) {
      console.error("Error downloading P45:", err);
      setError("Failed to download document. Please try again.");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Loading State */}
      {isLoadingProfile && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3">
            <Loader className="w-5 h-5 text-blue-600 animate-spin" />
            <p className="text-blue-900">Loading employee profile...</p>
          </div>
        </div>
      )}

      {/* Profile Error */}
      {profileError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-900 mb-1">
                Unable to Load Profile
              </p>
              <p className="text-sm text-red-800">
                Failed to fetch employee information. Please try refreshing the
                page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Not Terminated Warning */}
      {!isLoadingProfile && employeeNo && !isTerminated && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-900 mb-1">
                P45 Not Available
              </p>
              <p className="text-sm text-amber-800">
                P45 documents can only be generated for employees who have been
                terminated. This feature is currently not available for active
                employees.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Card */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-8 mb-6 text-white">
        <div className="flex items-start gap-4">
          <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
            <FileText className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">P45 Document</h1>
            <p className="text-indigo-100 text-lg">
              Generate and download your P45 tax form
            </p>
            {employeeName && (
              <p className="text-indigo-200 mt-2">
                Employee: {employeeName} ({employeeNo})
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-2">What is a P45?</p>
            <p className="mb-2">
              A P45 is a document given to you when you leave a job. It shows
              how much tax you've paid on your salary so far in the tax year.
            </p>
            <p className="text-blue-800">
              Your new employer will use this information to ensure you pay the
              correct amount of tax.
            </p>
          </div>
        </div>
      </div>

      {/* Generate Button Card */}
      <div className="bg-white rounded-xl shadow-md p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Generate P45 Document
        </h2>
        <p className="text-gray-600 mb-6">
          Click the button below to generate your P45 document. The document
          will be created in PDF format and can be viewed or downloaded.
        </p>

        <button
          onClick={handleGenerateP45}
          disabled={
            isGenerating || isLoadingProfile || !employeeNo || !isTerminated
          }
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
        >
          {isGenerating ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Generating P45...</span>
            </>
          ) : (
            <>
              <FileText className="w-5 h-5" />
              <span>Generate P45 Document</span>
            </>
          )}
        </button>
      </div>

      {/* Success Message */}
      {success && !error && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-green-900 mb-2">
                P45 Generated Successfully!
              </p>
              <p className="text-sm text-green-800 mb-4">
                Your P45 document has been generated. You can view it below or
                download it to your device.
              </p>
              <button
                onClick={handleDownload}
                className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download P45
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview */}
      {pdfUrl && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Document Preview
            </h2>
            <button
              onClick={handleDownload}
              className="text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <iframe
              src={pdfUrl}
              className="w-full h-[800px]"
              title="P45 Document Preview"
            />
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gray-50 rounded-xl p-6 mt-6">
        <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-indigo-600 font-bold">•</span>
            <span>
              If you're having trouble generating your P45, please contact HR
              support
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-600 font-bold">•</span>
            <span>
              Keep your P45 safe - you'll need to give it to your new employer
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-indigo-600 font-bold">•</span>
            <span>
              If you've lost your P45, contact HR to request a replacement
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};;

export default EmployeeP45;
